import type { ReactNode } from 'react';
import { Info } from 'lucide-react';
import { Badge, Card, CardContent, Tooltip, TooltipContent, TooltipTrigger, cn } from '@design-system';
import { approxMoney, money } from '../lib/format';

interface FigureProps {
  label: ReactNode;
  value: ReactNode;
  tone?: 'default' | 'positive' | 'attention';
  hint?: string;
  className?: string;
  containerClassName?: string;
}

export function Figure({ label, value, tone = 'default', hint, className, containerClassName }: FigureProps) {
  return (
    <div className={cn('flex h-full flex-col gap-1', containerClassName)}>
      <span className="text-muted-foreground flex w-full items-start gap-1 text-xs font-medium uppercase tracking-wide">
        <span className="min-w-0">{label}</span>
        {hint && (
          <Tooltip>
            <TooltipTrigger aria-label={`About ${typeof label === 'string' ? label.toLowerCase() : 'this figure'}`}>
              <Info className="size-3.5" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs normal-case tracking-normal">{hint}</TooltipContent>
          </Tooltip>
        )}
      </span>
      <strong
        className={cn(
          'mt-auto text-2xl font-semibold tabular-nums',
          tone === 'positive' && 'text-primary',
          tone === 'attention' && 'text-yellow-800',
          tone === 'default' && 'text-gray-950',
          className,
        )}
      >
        {value}
      </strong>
    </div>
  );
}

/** Shared size for values in a horizontal metric bar. */
export const metricFigureClassName = 'text-lg whitespace-nowrap';

/** Left-aligned metric row: equal columns, the same gaps as the care-scenario bar. */
export function MetricGrid({
  children,
  columns = 3,
  className,
}: {
  children: ReactNode;
  columns?: 2 | 3 | 4;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'grid gap-4 sm:gap-8',
        columns === 2 && 'sm:grid-cols-2',
        columns === 3 && 'sm:grid-cols-3',
        columns === 4 && 'sm:grid-cols-2 lg:grid-cols-4',
        className,
      )}
    >
      {children}
    </div>
  );
}

interface CoverageSplitProps {
  projectedPayout: number;
  outOfPocket: number;
  short?: boolean;
  children?: ReactNode;
}

/** What insurance is projected to pay for the care scenario, and what's left for the consumer. */
export function CoverageSplit({ projectedPayout, outOfPocket, short, children }: CoverageSplitProps) {
  return (
    <Card className="py-4">
      <CardContent className="flex flex-col gap-4 px-4">
        {children}
        <MetricGrid columns={2} className={children ? 'border-t pt-4' : undefined}>
          <Figure
            label={short ? 'Insurance could pay' : 'Projected LTC payout for this scenario'}
            value={money(projectedPayout)}
            tone="positive"
            className={metricFigureClassName}
          />
          <Figure
            label="Your expected out-of-pocket care cost"
            value={approxMoney(outOfPocket)}
            className={metricFigureClassName}
          />
        </MetricGrid>
      </CardContent>
    </Card>
  );
}

interface PolicyMaximumsProps {
  age: number;
  monthlyMax: { home: number; facility: number } | null;
  totalMax: number;
  className?: string;
  columns?: 2 | 3 | 4;
  children?: ReactNode;
}

function labelAtAge(before: string, age: number, after = '') {
  return (
    <>
      {before} <br />
      at age {age}
      {after}
    </>
  );
}

function monthlyMaxDisplay(monthlyMax: { home: number; facility: number }, age: number) {
  const home = money(monthlyMax.home);
  const facility = money(monthlyMax.facility);
  const same = home === facility;
  return {
    label: labelAtAge('Projected monthly benefit', age, same ? '' : ' (home/facility)'),
    value: same ? home : `${home} / ${facility}`,
  };
}

/** Monthly and lifetime limits from the same projection the coverage step uses. */
export function PolicyMaximums({
  age,
  monthlyMax,
  totalMax,
  className,
  columns,
  children,
}: PolicyMaximumsProps) {
  const monthly = monthlyMax ? monthlyMaxDisplay(monthlyMax, age) : null;
  const careBenefits = (
    <>
      {monthly && (
        <Figure label={monthly.label} value={monthly.value} className={metricFigureClassName} />
      )}
      <Figure
        label={labelAtAge('Projected lifetime max', age)}
        value={money(totalMax)}
        className={metricFigureClassName}
      />
    </>
  );

  if (children) {
    return (
      <MetricGrid columns={columns ?? 2}>
        {careBenefits}
        {children}
      </MetricGrid>
    );
  }

  return <div className={cn('flex flex-col gap-4', className)}>{careBenefits}</div>;
}

interface MaxBenefitsAtAgeProps {
  age: number;
  monthlyMax: { home: number; facility: number } | null;
  totalMax: number;
  className?: string;
  columns?: 2 | 3 | 4;
  children?: ReactNode;
}

/** The policy's limits in the year care starts, beyond what this scenario is projected to use. */
export function MaxBenefitsAtAge({
  age,
  monthlyMax,
  totalMax,
  className,
  columns,
  children,
}: MaxBenefitsAtAgeProps) {
  return (
    <section aria-label={`Policy maximums at age ${age}`}>
      <PolicyMaximums
        age={age}
        monthlyMax={monthlyMax}
        totalMax={totalMax}
        columns={columns}
        className={cn(monthlyMax ? 'sm:grid sm:grid-cols-2' : undefined, className)}
      >
        {children}
      </PolicyMaximums>
    </section>
  );
}

function monthlyBenefitRows(monthlyMax: { home: number; facility: number }) {
  const home = money(monthlyMax.home);
  const facility = money(monthlyMax.facility);
  if (home === facility) return [{ label: 'Monthly Benefit', value: home }];
  return [
    { label: 'Monthly Benefit, home', value: home },
    { label: 'Monthly Benefit, facility', value: facility },
  ];
}

/** The option cards' compact limits: the care age once, as a pill, then one short row per amount. */
export function PolicyBenefitRows({
  age,
  monthlyMax,
  totalMax,
}: {
  age: number;
  monthlyMax: { home: number; facility: number } | null;
  totalMax: number;
}) {
  const rows = [
    ...(monthlyMax ? monthlyBenefitRows(monthlyMax) : []),
    { label: 'Lifetime Max', value: money(totalMax) },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground text-xs font-medium uppercase tracking-wide">Benefits</span>
        <Badge>At age {age}</Badge>
      </div>
      <dl className="flex flex-col gap-3">
        {rows.map(row => (
          <div key={row.label} className="flex items-baseline justify-between gap-3">
            <dt className="text-sm text-gray-700">{row.label}</dt>
            <dd className="font-semibold tabular-nums text-gray-950">{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export interface DetailRow {
  label: ReactNode;
  value: ReactNode;
  tone?: 'default' | 'positive';
}

export function DetailList({ rows }: { rows: DetailRow[] }) {
  return (
    <dl className="divide-y rounded-lg border bg-white">
      {rows.map((row, index) => (
        <div key={index} className="flex items-start justify-between gap-4 px-4 py-3 text-sm">
          <dt className="text-muted-foreground">{row.label}</dt>
          <dd
            className={cn(
              'text-right font-medium tabular-nums',
              row.tone === 'positive' ? 'text-green-800' : 'text-gray-950',
            )}
          >
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** A bar showing how much of a care bill insurance pays. */
export function PaymentSplitBar({ total, insurance, label }: { total: number; insurance: number; label: string }) {
  const share = (part: number) => (total > 0 ? `${Math.min(100, (part / total) * 100)}%` : '0%');

  return (
    <div role="img" aria-label={label} className="flex h-3 w-full overflow-hidden rounded-full bg-gray-200">
      <span className="bg-primary h-full transition-[width] duration-300 ease-out" style={{ width: share(insurance) }} />
    </div>
  );
}
